import React from "react";
import moment from "moment";
import {Button} from "reactstrap";
import Checkbox from "@material-ui/core/Checkbox";
import Edit from "@material-ui/icons/Edit";
import {
	formatInvoiceTotal,
	formatPaymentType,
	getFreshdeskTicketUrl,
	getTicketFinancial,
} from "./financialDisplay";

/**
 * @param {{
 *   onEditFinancial: (ticket: Tickets_Object) => void,
 *   onToggleValid: (ticket: Tickets_Object, financial: Tickets_Financials_Object) => void,
 * }} handlers
 */
export const buildAuditWorkOrderColumns = ({onEditFinancial, onToggleValid}) => [
	{
		Header: "Date",
		id: "appointment_start",
		accessor: (obj) => moment(obj.appointment_start).format("MM/DD/YYYY"),
	},
	{
		Header: "Time",
		id: "appointment_end",
		accessor: (obj) =>
			obj.appointment_start
				? moment(obj.appointment_start).local().format("HHmm") +
				  "-" +
				  moment(obj.appointment_end).local().format("HHmm")
				: "",
	},
	{
		Header: "Employee",
		id: "creator",
		accessor: (obj) =>
			obj.field_engineers
				? `${obj.field_engineers.name_first} ${obj.field_engineers.name_last}`
				: "",
	},
	{
		Header: "Rate / Hr",
		id: "rate",
		accessor: () => "$125",
	},
	{
		Header: "End User",
		id: "customer",
		accessor: (obj) => (obj.customers ? obj.customers.name : ""),
	},
	{
		Header: "Description",
		id: "summary",
		accessor: (obj) => obj.summary,
	},
	{
		Header: "Location",
		id: "location",
		accessor: (obj) =>
			obj.customers_locations
				? `${obj.customers_locations.city}, ${obj.customers_locations.state}`
				: "",
	},
	{
		sortable: false,
		Header: "FreshDesk",
		id: "fresh_ticket",
		accessor: (obj) => getFreshdeskTicketUrl(obj.fresh_ticket),
		Cell: (props) => (
			<span>
				<a href={getFreshdeskTicketUrl(props.original.fresh_ticket)}>
					{props.original.fresh_ticket}
				</a>
			</span>
		),
	},
	{
		Header: "Total Invoice",
		id: "invoice",
		accessor: (obj) => formatInvoiceTotal(getTicketFinancial(obj)),
	},
	{
		Header: "Type",
		id: "type",
		accessor: (obj) => formatPaymentType(getTicketFinancial(obj)?.payment_type),
	},
	{
		Header: "Edit",
		id: "edit_financial",
		className: "button-cell",
		filterable: false,
		sortable: false,
		width: 50,
		accessor: (obj) => getTicketFinancial(obj)?.pkey,
		Cell: (props) => {
			const financial = getTicketFinancial(props.original);
			if (!financial?.pkey) return null;
			return (
				<Button
					color="green-meadow"
					size="sm"
					block
					onClick={() => onEditFinancial(props.original)}
				>
					<Edit fontSize="small" />
				</Button>
			);
		},
	},
	{
		Header: "Valid",
		id: "valid",
		className: "button-cell",
		filterable: false,
		sortable: false,
		width: 60,
		accessor: (obj) => !!getTicketFinancial(obj)?.approved,
		Cell: (props) => {
			const financial = getTicketFinancial(props.original);
			if (!financial?.pkey) return null;
			return (
				<div style={{textAlign: "center"}}>
					<Checkbox
						checked={!!financial.approved}
						onChange={() => onToggleValid(props.original, financial)}
						color="primary"
					/>
				</div>
			);
		},
	},
	{
		Header: "Notes",
		id: "notes",
		accessor: (obj) => getTicketFinancial(obj)?.notes?.replace(/[\r\n]+/g, " "),
	},
];

/**
 * Condensed audit columns for the single-FE weekly view.
 * @param {{
 *   onEditFinancial: (ticket: Tickets_Object) => void,
 *   onToggleValid: (ticket: Tickets_Object, financial: Tickets_Financials_Object) => void,
 * }} handlers
 */
export const buildFeWeeklyAuditColumns = ({onEditFinancial, onToggleValid}) => [
	{
		Header: "Date",
		id: "appointment_start",
		width: 120,
		accessor: (obj) =>
			obj.appointment_start
				? `${moment(obj.appointment_start).format("MM/DD")} ${moment(obj.appointment_start).local().format("HHmm")}-${moment(obj.appointment_end).local().format("HHmm")}`
				: "",
	},
	{
		Header: "Customer",
		id: "customer",
		width: 150,
		accessor: (obj) => (obj.customers ? obj.customers.name : ""),
	},
	{
		Header: "Description",
		id: "summary",
		width: 200,
		resizable: false,
		accessor: (obj) => obj.summary,
	},
	{
		Header: "Location",
		id: "location",
		width: 140,
		accessor: (obj) =>
			obj.customers_locations
				? `${obj.customers_locations.city}, ${obj.customers_locations.state}`
				: "",
	},
	{
		sortable: false,
		Header: "FD#",
		id: "fresh_ticket",
		width: 80,
		accessor: (obj) => getFreshdeskTicketUrl(obj.fresh_ticket),
		Cell: (props) => (
			<a href={getFreshdeskTicketUrl(props.original.fresh_ticket)}>
				{props.original.fresh_ticket}
			</a>
		),
	},
	{
		Header: "Total",
		id: "invoice",
		width: 150,
		accessor: (obj) => formatInvoiceTotal(getTicketFinancial(obj)),
	},
	{
		Header: "Type",
		id: "type",
		width: 120,
		accessor: (obj) => formatPaymentType(getTicketFinancial(obj)?.payment_type),
	},
	{
		Header: "",
		id: "edit_financial",
		className: "button-cell",
		filterable: false,
		sortable: false,
		resizable: false,
		width: 42,
		accessor: (obj) => getTicketFinancial(obj)?.pkey,
		Cell: (props) => {
			const financial = getTicketFinancial(props.original);
			if (!financial?.pkey) return null;
			return (
				<Button
					color="green-meadow"
					size="sm"
					style={{padding: "0 4px", lineHeight: 1}}
					onClick={() => onEditFinancial(props.original)}
				>
					<Edit style={{fontSize: 14}} />
				</Button>
			);
		},
	},
	{
		Header: "OK",
		id: "valid",
		className: "button-cell",
		filterable: false,
		sortable: false,
		resizable: false,
		width: 46,
		accessor: (obj) => !!getTicketFinancial(obj)?.approved,
		Cell: (props) => {
			const financial = getTicketFinancial(props.original);
			if (!financial?.pkey) return null;
			return (
				<Checkbox
					size="small"
					checked={!!financial.approved}
					onChange={() => onToggleValid(props.original, financial)}
					color="primary"
					style={{padding: 0}}
				/>
			);
		},
	},
	{
		Header: "Notes",
		id: "notes",
		// width: 400,
		accessor: (obj) => getTicketFinancial(obj)?.notes?.replace(/[\r\n]+/g, " "),
	},
];
